package com.clany.backoffice.data.repository

import com.clany.backoffice.data.local.TokenStorage
import com.clany.backoffice.data.network.AuthBodyCalls
import com.clany.backoffice.data.network.CLIENT_ID
import com.clany.backoffice.data.network.KEYCLOAK_AUTH_URL
import com.clany.backoffice.data.network.LOGIN_URL
import com.clany.backoffice.data.network.LOGOUT_URL
import com.clany.backoffice.data.network.NetworkClient
import com.clany.backoffice.data.network.computeCodeChallenge
import com.clany.backoffice.data.network.generateCodeVerifier
import com.clany.backoffice.data.network.getRedirectUri
import com.clany.backoffice.data.network.getSessionItem
import com.clany.backoffice.data.network.redirectToUrl
import com.clany.backoffice.data.network.removeSessionItem
import com.clany.backoffice.data.network.storeSessionItem
import com.clany.backoffice.data.network.response.ServiceAuthEntity
import com.clany.backoffice.domain.repository.AuthRepository
import io.ktor.http.HttpMethod

import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import dev.zacsweers.metro.SingleIn
import com.clany.backoffice.di.AppScope
import kotlinx.coroutines.flow.StateFlow
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

@SingleIn(AppScope::class)
@ContributesBinding(AppScope::class)
@Inject
class AuthRepositoryImpl(
    private val networkClient: NetworkClient,
    private val tokenStorage: TokenStorage
) : AuthRepository {
    override val authState: StateFlow<Boolean> = tokenStorage.authState
    
    override suspend fun login(email: String, password: String): Result<Boolean> {
        val result = networkClient.publicRequest<ServiceAuthEntity>(
            url = LOGIN_URL,
            method = HttpMethod.Post,
            body = AuthBodyCalls.accessTokenBody(email, password, CLIENT_ID),
            isFormUrlEncoded = true
        )

        result.onFailure { error ->
            println("Login failed: ${error.message}")
            if (error is kotlinx.serialization.SerializationException) {
                println("Serialization error details: ${error.stackTraceToString()}")
            }
        }

        return result.fold(
            onSuccess = { authEntity ->
                if (authEntity.accessToken != null && authEntity.refreshToken != null) {
                    if (!hasBackofficeRole(authEntity.accessToken)) {
                        return@fold Result.failure(Exception("Acceso no autorizado. Necesitas el rol Backoffice."))
                    }
                    tokenStorage.saveToken(authEntity.accessToken)
                    tokenStorage.saveRefreshToken(authEntity.refreshToken)
                    tokenStorage.saveExpiresIn(authEntity.expiresIn?.toLong() ?: 0L)
                    tokenStorage.saveRefreshExpiresIn(authEntity.refreshExpiresIn?.toLong() ?: 0L)
                    authEntity.idToken?.let { tokenStorage.saveIdToken(it) }
                    Result.success(true)
                } else {
                    val errorMsg = authEntity.errorDescription ?: authEntity.error ?: "Unknown authentication error"
                    println("Authentication error: $errorMsg")
                    Result.failure(Exception(errorMsg))
                }
            },
            onFailure = { error ->
                Result.failure(error)
            }
        )
    }

    override suspend fun logout(): Result<Unit> {
        val idToken = tokenStorage.getIdToken()
        tokenStorage.clearAll()
        storeSessionItem("logged_out", "true")
        redirectToKeycloakLogout(idToken)
        return Result.success(Unit)
    }

    override suspend fun isLoggedIn(): Boolean {
        return !tokenStorage.getToken().isNullOrBlank()
    }

    override fun initiateLogin() {
        val verifier = generateCodeVerifier()
        val challenge = computeCodeChallenge(verifier)
        storeSessionItem("pkce_verifier", verifier)
        val redirectUri = getRedirectUri()
        val authUrl = buildString {
            append(KEYCLOAK_AUTH_URL)
            append("?client_id=").append(CLIENT_ID)
            append("&redirect_uri=").append(redirectUri)
            append("&response_type=code")
            append("&scope=openid")
            append("&code_challenge=").append(challenge)
            append("&code_challenge_method=S256")
        }
        redirectToUrl(authUrl)
    }

    @OptIn(kotlin.io.encoding.ExperimentalEncodingApi::class)
    private fun hasBackofficeRole(accessToken: String): Boolean {
        return try {
            val parts = accessToken.split(".")
            if (parts.size < 2) return false
            val payload = parts[1]
            val padded = payload + "=".repeat((4 - payload.length % 4) % 4)
            val json = kotlin.io.encoding.Base64.decode(padded).decodeToString()
            val obj = kotlinx.serialization.json.Json.parseToJsonElement(json).jsonObject
            val roles = obj["realm_access"]?.jsonObject
                ?.get("roles")?.jsonArray
                ?.map { it.jsonPrimitive.content }
            roles?.contains("Backoffice") == true
        } catch (e: Exception) {
            false
        }
    }

    override suspend fun handleOAuthCode(code: String): Result<Boolean> {
        val verifier = getSessionItem("pkce_verifier")
            ?: return Result.failure(Exception("No PKCE verifier found in session"))
        removeSessionItem("pkce_verifier")
        val redirectUri = getRedirectUri()

        val result = networkClient.publicRequest<ServiceAuthEntity>(
            url = LOGIN_URL,
            method = HttpMethod.Post,
            body = AuthBodyCalls.authCodeExchangeBody(code, CLIENT_ID, redirectUri, verifier),
            isFormUrlEncoded = true
        )

        return result.fold(
            onSuccess = { authEntity ->
                if (authEntity.accessToken != null) {
                    if (!hasBackofficeRole(authEntity.accessToken)) {
                        redirectToKeycloakLogout()
                        return@fold Result.failure(Exception("Acceso no autorizado. Necesitas el rol Backoffice."))
                    }
                    tokenStorage.saveToken(authEntity.accessToken)
                    tokenStorage.saveRefreshToken(authEntity.refreshToken ?: "")
                    tokenStorage.saveExpiresIn(authEntity.expiresIn?.toLong() ?: 0L)
                    tokenStorage.saveRefreshExpiresIn(authEntity.refreshExpiresIn?.toLong() ?: 0L)
                    authEntity.idToken?.let { tokenStorage.saveIdToken(it) }
                    Result.success(true)
                } else {
                    val msg = authEntity.errorDescription ?: authEntity.error ?: "Code exchange failed"
                    redirectToKeycloakLogout()
                    Result.failure(Exception(msg))
                }
            },
            onFailure = {
                redirectToKeycloakLogout()
                Result.failure(it)
            }
        )
    }

    private fun redirectToKeycloakLogout(idToken: String? = null) {
        val redirectUri = getRedirectUri()
        val logoutUrl = buildString {
            append(LOGOUT_URL)
            append("?client_id=").append(CLIENT_ID)
            append("&post_logout_redirect_uri=").append(redirectUri)
            if (idToken != null) {
                append("&id_token_hint=").append(idToken)
            }
        }
        redirectToUrl(logoutUrl)
    }
}
